import { trackUserInteractionWithTiming } from "@vp/track-user-interaction";
import { MerchandisingProductView } from "../types/ContentfulTypes";
import { analyticsPageName } from "./analyticsConfig";
import {
  ProductOptionsSelectedEventProperties,
  ProductOptionsSelectedEventProductOption,
  trackAttributeSelected,
  trackProductOptionsSelected,
} from "@vp/product-options-tracking";
import { Logger } from "../logging/logger";
import { BreakdownPrice, LowestPrice } from "@vp/hamilton-service-clients/dist/src/types/Pricing";
import { ProductBuilderOptions } from "../types/ProductBuilderTypes";
import { isOptionVisible, showTextBoxSelector } from "./ProductBuilderHelper";
import RangeType from "../types/RangeTypes";

export enum MediaEngagedCategory {
  Video = "Video",
  Image = "Image",
}

export enum TemplateDownloadedFileType {
  PDF = "PDF",
  SVG = "SVG",
  ADOBE_ILLUSTRATOR = "Adobe Illustrator",
  DESIGN_INSTRUCTIONS = "Design instructions",
}

export type EventNameType =
  | "next cta clicked"
  | "user changed selections"
  | "product builder loaded"
  | "Experiment Clicked";

export default class TrackingUtil {
  public static editorDownloadEvent(props: {
    btnLabel: string;
    mpvId: string;
    localizedProductName: string;
    coreProductId: string;
    isFromTab: boolean;
  }) {
    const { btnLabel, mpvId, localizedProductName, coreProductId, isFromTab } = props;
    const eventName = "Button Clicked";

    TrackingUtil.sendTracking(eventName, () => {
      (window.tracking as any).track(eventName, {
        category: "Product Page",
        label: btnLabel,
        eventDetail: isFromTab ? "Tab" : "Top",
        pageSection: "Product Page",
        pageStage: "Discover",
        pageName: `${mpvId}:Product Page`,
        product_id: mpvId,
        name: localizedProductName,
        core_product_id: coreProductId,
      });
    });
  }

  public static trackCtaClick(props: {
    ctaType: string;
    pageSection: string;
    destination: string;
    mpvId: string;
    position: number;
    selections: Record<string, string>;
  }) {
    const { pageSection, ctaType, destination, mpvId, position, selections } = props;
    const eventName = "Navigation Clicked";

    TrackingUtil.sendTracking(eventName, () => {
      (window.tracking as any).track(eventName, {
        category: "Product Page",
        label: `Content:${pageSection}`,
        eventDetail: `${window.location.pathname};${destination};${pageSection};${ctaType}-CTA`,
        navigationDetail: `${ctaType}-CTA:${position}`,
        pageSection,
        pageStage: "Discover",
        pageName: analyticsPageName(mpvId),
        variant: this.formatVariant(selections),
      });
    });
  }

  public static trackAddToCart(props: {
    pageSection: string;
    mpvId: string;
    selections: Record<string, string>;
    productKey: string;
    version: number;
    price: number;
    listPrice: number;
    selectedQuantity: number;
  }) {
    const {
      pageSection,
      mpvId,
      selections,
      productKey,
      version,
      price,
      listPrice,
      selectedQuantity,
    } = props;

    TrackingUtil.sendTracking("Product Added", () => {
      (window.tracking as any).track("Product Added", {
        category: "EnhancedEcommerce",
        label: "Product Added",
        pageSection,
        pageStage: "Discover",
        pageName: analyticsPageName(mpvId),
        variant: this.formatVariant(selections),
        product_id: mpvId,
        core_product_id: productKey,
        core_product_version: version,
        price,
        listPrice,
        quantity: 1,
        sales_quantity: selectedQuantity,
        discount: listPrice - price,
      });
    });
  }

  private static formatVariant(selections: Record<string, string>) {
    let variant = "";
    Object.keys(selections || {}).forEach((optionKey: string, index: number) => {
      variant = variant + `${index === 0 ? "" : "_"}${optionKey}:${selections[optionKey]}`;
    });
    return variant;
  }

  public static trackTabChange(tabType: string, mpvId: string) {
    const eventName = "Tab Selected";
    TrackingUtil.sendTracking(eventName, () => {
      (window.tracking as any).track(eventName, {
        category: "Product Page",
        label: `${tabType}`,
        pageSection: "Product Tabs",
        pageStage: "Discover",
        pageName: analyticsPageName(mpvId),
      });
    });
  }

  public static trackTermsOfUseToggle(isOpen: boolean, mpvId: string) {
    const eventName = `Fly-Out ${isOpen ? "Viewed" : "Closed"}`;
    TrackingUtil.sendTracking(eventName, () => {
      (window.tracking as any).track(eventName, {
        category: "Fly-Out",
        label: "Terms of Use Fly-Out",
        pageSection: "Link to Terms of Use",
        pageStage: "Discover",
        pageName: analyticsPageName(mpvId),
      });
    });
  }

  public static trackFilterSelection(filterKey: string) {
    trackAttributeSelected({
      category: "Product Page",
      label: `${filterKey}:Design Specifications`,
    });
  }

  public static trackBuilderSelection(props: { optionKey: string; valueKey: string }) {
    const { optionKey, valueKey } = props;
    trackAttributeSelected({
      category: "Product Page",
      label: optionKey,
      eventDetail: valueKey,
    });
  }

  public static trackDeliveryOptionsEvent(isOpening: boolean, mpvId: string) {
    const eventName = isOpening ? "Fly-Out Viewed" : "Fly-Out Closed";

    TrackingUtil.sendTracking(eventName, () => {
      (window.tracking as any).track(eventName, {
        category: "Fly-Out",
        label: "Delivery Options Fly-Out",
        pageSection: "Product Builder",
        pageStage: "Discover",
        pageName: analyticsPageName(mpvId),
      });
    });
  }

  public static trackFscOpen(props: { elementClicked: "banner" | "logo"; mpvId: string }) {
    const { elementClicked, mpvId } = props;
    const eventName = "Fly-Out Viewed";
    TrackingUtil.sendTracking(eventName, () => {
      (window.tracking as any).track(eventName, {
        category: "Fly-Out",
        label: elementClicked === "banner" ? "FSC Fly-Out Link" : "FSC Fly-Out Icon",
        pageSection: elementClicked === "banner" ? "Eco Links" : "Product Builder",
        pageStage: "Discover",
        pageName: analyticsPageName(mpvId),
        eventDetail: elementClicked === "banner" ? "Learn More Link clicked" : "FSC Icon clicked",
      });
    });
  }

  public static trackFscClose(elementClicked: "banner" | "logo" | undefined, mpvId: string) {
    const eventName = "Fly-Out Closed";
    TrackingUtil.sendTracking(eventName, () => {
      (window.tracking as any).track(eventName, {
        category: "Fly-Out",
        label: elementClicked === "banner" ? "FSC Fly-Out Link" : "FSC Fly-Out Icon",
        pageSection: elementClicked === "banner" ? "Eco Links" : "Product Builder",
        pageStage: "Discover",
        pageName: analyticsPageName(mpvId),
      });
    });
  }

  public static trackFscLinkClick(
    elementClicked: "banner" | "logo" | undefined,
    mpvId: string,
    linkText: string
  ) {
    const eventName = "Fly-Out Clicked";
    TrackingUtil.sendTracking(eventName, () => {
      (window.tracking as any).track(eventName, {
        category: "Fly-Out",
        label: elementClicked === "banner" ? "FSC Fly-Out Link" : "FSC Fly-Out Icon",
        pageSection: elementClicked === "banner" ? "Eco Links" : "Product Builder",
        pageStage: "Discover",
        pageName: analyticsPageName(mpvId),
        eventDetail: linkText.includes("FSC")
          ? "FSC Certification"
          : "Commitment to sustainability",
      });
    });
  }

  public static trackDesignServicesClick(mpvId: string, destinationUrl: string) {
    TrackingUtil.sendTracking("Navigation Clicked", () => {
      (window.tracking as any).track("Navigation Clicked", {
        category: "Product Page",
        label: "Content:Design Services Tiles",
        pageSection: "Link to other Services",
        pageStage: "Discover",
        pageName: analyticsPageName(mpvId),
        navigationDetail: "See Design Services",
        eventDetail: `${window.location.pathname};${destinationUrl};Design Services Tiles;Design Services-CTA`,
      });
    });
  }

  public static trackExperimentViewed(
    mpvId: string,
    options: {
      label: string;
      pageStage: string;
      experimentId?: string;
      experimentName?: string;
      variationId?: string;
      variationName?: string;
    }
  ) {
    const eventName = "Experiment Viewed";
    TrackingUtil.sendTracking(eventName, () => {
      (window.tracking as any).track(eventName, {
        label: options.label,
        pageSection: "Product Builder",
        pageStage: options.pageStage,
        pageName: analyticsPageName(mpvId),
        experimentId: options.experimentId,
        experimentName: options.experimentName,
        variationId: options.variationId,
        variationName: options.variationName,
      });
    });
  }

  public static trackExperimentClicked(
    mpv: MerchandisingProductView,
    options: {
      label: string;
      pageSection: string;
      pageStage: string;
      eventDetail?: string;
      navigationDetail?: string;
      experimentId?: string;
      experimentName?: string;
      variationId?: string;
      variationName?: string;
      experimentDetail?: string;
    }
  ) {
    const eventName: EventNameType = "Experiment Clicked";
    TrackingUtil.sendTracking(eventName, () => {
      (window.tracking as any).track(eventName, {
        eventDetail: options.eventDetail,
        navigationDetail: options.navigationDetail,
        label: options.label,
        pageSection: options.pageSection,
        pageStage: options.pageStage,
        pageName: analyticsPageName(mpv.mpvId),
        trackingTenant: "vistaprint",
        product_id: mpv.mpvId,
        name: mpv.name,
        core_product_id: mpv.coreProductId,
        core_product_version: mpv.version,
        experimentId: options.experimentId,
        experimentName: options.experimentName,
        variationId: options.variationId,
        variationName: options.variationName,
        experimentDetail: options.experimentDetail,
      });
    });
  }

  public static sendTracking(eventName: string, callback: Function, counter: number = 0) {
    const windowTracking = window.tracking as any;
    if (windowTracking && windowTracking.track && windowTracking.page) {
      if (counter !== 0) {
        Logger.instance.info("Tracking success after retry", {
          eventName,
          counter,
        });
      }
      callback();
    } else {
      if (counter < 10) {
        setTimeout(() => TrackingUtil.sendTracking(eventName, callback, ++counter), 1000);
      } else {
        Logger.instance.error("ERROR: Tracking is not initialized after 10s.", { eventName });
      }
    }
  }

  public static trackCtaTiming(timingMillis: number) {
    this.trackTiming(timingMillis, "next cta clicked");
  }

  public static trackTiming(timingMillis: number, eventName: string) {
    trackUserInteractionWithTiming({
      eventName,
      timing: timingMillis,
    });
  }

  public static trackSpecsTemplateLoaded(
    timingMillis: number,
    eventName: "product specs loaded" | "product specs changed"
  ) {
    trackUserInteractionWithTiming({
      eventName,
      timing: timingMillis,
    });
  }

  public static trackUpdatedSelections(
    mpvId: string,
    productName: string,
    productKey: string,
    selections: Record<string, string>,
    initialSelections: Record<string, string>,
    initialQuantity: number,
    selectedQuantity: number,
    prices: Record<string, LowestPrice>,
    initialPrices: Record<string, LowestPrice> | undefined,
    productBuilderOptions: ProductBuilderOptions,
    compatibleRanges?: Record<string, RangeType[]>
  ) {
    const allAvailableOptions = (productBuilderOptions.selections || [])
      .map(selection => selection?.property.key)
      .filter(optionKey => !!optionKey);
    const initialBreakdown = initialPrices?.[`${initialQuantity}`].breakdown ?? [];
    const currentBreakdown = prices[`${selectedQuantity}`].breakdown;
    const productOptionsForTracking = this.getProductOptionsForTracking(
      allAvailableOptions,
      productBuilderOptions,
      selections,
      initialSelections,
      selectedQuantity,
      initialQuantity,
      initialBreakdown,
      currentBreakdown,
      compatibleRanges
    );

    const initialDiscountedPrice =
      initialPrices?.[`${initialQuantity}`].totalDiscountedPrice.taxed ?? 0;
    const discountedPrice = prices[`${selectedQuantity}`].totalDiscountedPrice.taxed;
    const listPrice = prices[`${selectedQuantity}`].totalListPrice.taxed;
    const variant = productOptionsForTracking
      .map(option => `${option.key}:${option.value}`)
      .join("_");

    trackProductOptionsSelected({
      category: "Product Page",
      label: "Product page view",
      product_id: mpvId,
      name: productName,
      core_product_id: productKey,
      productOptions: productOptionsForTracking,
      productUpdate: this.determineUpsellOrDownsell(initialDiscountedPrice, discountedPrice),
      price: discountedPrice,
      list_price: listPrice,
      sales_quantity: selectedQuantity,
      variant,
    } as ProductOptionsSelectedEventProperties);
  }

  private static getProductOptionsForTracking(
    allAvailableOptions: string[],
    productBuilderOptions: ProductBuilderOptions,
    selections: Record<string, string>,
    initialSelections: Record<string, string>,
    selectedQuantity: number,
    initialQuantity: number,
    initialBreakdown: BreakdownPrice[],
    currentBreakdown: BreakdownPrice[],
    compatibleRanges?: Record<string, RangeType[]>
  ) {
    const allOptionsWithEmptySelections = allAvailableOptions.reduce((result, option) => {
      result[option] = "";
      return result;
    }, {} as Record<string, string>);
    const allOptionsWithSelections = Object.assign({}, allOptionsWithEmptySelections, selections);
    const productOptionsForTracking: ProductOptionsSelectedEventProductOption[] = Object.entries(
      allOptionsWithSelections
    ).map(([optionKey, optionValue]) => {
      let isUpdated = true;
      let update: "nochange" | "upsell" | "downsell" = "nochange";
      let isVisible = true;
      const optionFromFind = productBuilderOptions.selections.find(
        selection => selection?.property?.key === optionKey
      );
      if (
        !isOptionVisible(optionFromFind) ||
        (optionFromFind?.selectionStyle === "Text box" &&
          !showTextBoxSelector(compatibleRanges?.[optionKey] ?? [], true))
      ) {
        isVisible = false;
      }

      // When optionValue is empty string, it means that
      // option is displayed but customer does not make a selection on it or if there is no selection unwinded.
      if (
        (initialSelections[optionKey] && initialSelections[optionKey] === optionValue) ||
        (!initialSelections[optionKey] && !allOptionsWithSelections[optionKey])
      ) {
        isUpdated = false;
      }

      if (isUpdated) {
        const initialOptionPrice = this.getBreakdownOptionPrice(initialBreakdown, optionKey);
        const currentOptionPrice = this.getBreakdownOptionPrice(currentBreakdown, optionKey);
        update = this.determineUpsellOrDownsell(initialOptionPrice, currentOptionPrice);
      }

      return {
        key: optionKey,
        value: optionValue,
        isUpdated,
        update,
        isVisible,
      };
    });
    productOptionsForTracking.push({
      key: "Quantity",
      value: selectedQuantity.toString(),
      isUpdated: selectedQuantity !== initialQuantity,
      update: this.getProductUpdateForQuantity(selectedQuantity, initialQuantity),
      isVisible: true,
    });

    return productOptionsForTracking;
  }

  private static getBreakdownOptionPrice(breakdown: BreakdownPrice[], optionKey: string) {
    return (
      breakdown.find(breakdownOption => breakdownOption.name === optionKey)?.listPrice.taxed ?? 0
    );
  }

  private static determineUpsellOrDownsell(initialPrice: number, currentPrice: number) {
    if (currentPrice > initialPrice) {
      return "upsell";
    } else if (currentPrice < initialPrice) {
      return "downsell";
    }
    return "nochange";
  }

  private static getProductUpdateForQuantity(selectedQuantity: number, initialQuantity: number) {
    if (selectedQuantity > initialQuantity) {
      return "upsell";
    } else if (selectedQuantity < initialQuantity) {
      return "downsell";
    } else {
      return "nochange";
    }
  }

  public static trackVortexHeroGalleryViewed(
    mpv: MerchandisingProductView,
    options: {
      pageSection: string;
      label: string;
      eventDetail: string;
    }
  ) {
    const eventName = "Vortex 3D Toggle Button Viewed";
    TrackingUtil.sendTracking(eventName, () => {
      (window.tracking as any).track(eventName, {
        eventDetail: options.eventDetail,
        label: options.label,
        eventName,
        product_id: mpv.mpvId,
        name: mpv.name,
        pageSection: options.pageSection,
        pageStage: "Discover",
        pageName: analyticsPageName(mpv.mpvId),
        trackingTenant: "vistaprint",
      });
    });
  }

  public static trackVortexHeroGalleryClicked(
    mpv: MerchandisingProductView,
    options: {
      pageSection: string;
      label: string;
      eventDetail: string;
    }
  ) {
    const eventName = "Vortex 3D Toggle Button Clicked";
    TrackingUtil.sendTracking(eventName, () => {
      (window.tracking as any).track(eventName, {
        eventDetail: options.eventDetail,
        label: options.label,
        pageSection: options.pageSection,
        pageStage: "Discover",
        pageName: analyticsPageName(mpv.mpvId),
        trackingTenant: "vistaprint",
        product_id: mpv.mpvId,
        name: mpv.name,
      });
    });
  }

  public static trackHeroGalleryThumbnailClicked(
    position: number,
    category: MediaEngagedCategory,
    mpv: MerchandisingProductView
  ): void {
    const eventName = "Media Engaged";
    TrackingUtil.sendTracking(eventName, () => {
      (window.tracking as any).track(eventName, {
        category,
        label: "Hero Gallery Thumbnail",
        eventDetail: `position_${position}`,
        pageStage: "Discover",
        pageSection: "Product Page",
        pageName: analyticsPageName(mpv.mpvId),
        product_id: mpv.mpvId,
        name: mpv.name,
        core_product_id: mpv.coreProductId,
        core_product_version: mpv.version,
      });
    });
  }

  public static trackTemplateFileDownloaded(
    fileType: TemplateDownloadedFileType,
    mpv: MerchandisingProductView
  ): void {
    const eventName = "File Downloaded";
    TrackingUtil.sendTracking(eventName, () => {
      (window.tracking as any).track(eventName, {
        category: "Product Page",
        label: fileType,
        pageStage: "Discover",
        pageSection: "Product Page",
        pageName: analyticsPageName(mpv.mpvId),
        product_id: mpv.mpvId,
        name: mpv.name,
        core_product_id: mpv.coreProductId,
        core_product_version: mpv.version,
      });
    });
  }

  public static trackDesignSpecsCalloutClick(mpv: MerchandisingProductView): void {
    TrackingUtil.trackExperimentClicked(mpv, {
      label: "specsAndTemplatesCallout",
      pageSection: "Product Page",
      pageStage: "Discover",
    });
  }
}
